import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Home, Wrench, Building, Plug, Shield, Bolt, PowerIcon } from 'lucide-react';
import { IMAGE_PATHS } from '@/utils/imagePaths';
import { RemoteImage } from '@/components/common/ResponsiveImage';
import { supabaseImgSrc, supabaseSrcSet } from '@/lib/images';

const PANEL_PATH = "dc33fa83-da83-438f-bf18-847902cadea2.png" as const;
const GEN_PATH = "8e0f4711-0322-4b4b-8734-c21cf5dd937d.png" as const;

const ServicesOverview = () => {
  const services = [
    {
      id: 1,
      title: "New Construction",
      description: "Complete electrical system design and installation for new residential and commercial buildings.",
      icon: <Home className="h-10 w-10 text-orange" />,
      image: IMAGE_PATHS["e25f76f3-a488-4394-b062-23ec9f7e183e.png"],
      link: "/services/new-construction"
    },
    {
      id: 2,
      title: "Remodels",
      description: "Electrical upgrades, rewiring, panel replacements, and modern lighting solutions for renovations.",
      icon: <Wrench className="h-10 w-10 text-orange" />,
      link: "/services/remodels"
    },
    {
      id: 3,
      title: "Commercial",
      description: "Comprehensive electrical services for offices, retail, warehouses, and other commercial properties.",
      icon: <Building className="h-10 w-10 text-orange" />,
      link: "/services/commercial"
    },
    {
      id: 4,
      title: "Residential",
      description: "From simple repairs to complete home electrical systems, including smart home technology.",
      icon: <Home className="h-10 w-10 text-orange" />,
      image: IMAGE_PATHS["bf65d4be-1a7b-472c-953c-1d605b642646.png"],
      link: "/services/residential"
    },
    {
      id: 5,
      title: "Generator Installation",
      description: "Backup power solutions for homes and businesses to keep you operational during outages.",
      icon: <Bolt className="h-10 w-10 text-orange" />,
      image: IMAGE_PATHS["8e0f4711-0322-4b4b-8734-c21cf5dd937d.png"],
      link: "/services/generators"
    },
    {
      id: 6,
      title: "EV Charging Stations",
      description: "Installation of electric vehicle charging stations for residential and commercial properties.",
      icon: <Plug className="h-10 w-10 text-orange" />,
      image: IMAGE_PATHS["32f09723-a950-4fbc-95ab-ec37a267ef6f.png"],
      link: "/services/ev-charging"
    },
    {
      id: 7,
      title: "Panel Upgrades",
      description: "Modernize your electrical panel to meet today's power demands and improve safety.",
      icon: <Shield className="h-10 w-10 text-orange" />,
      image: IMAGE_PATHS["dc33fa83-da83-438f-bf18-847902cadea2.png"],
      link: "/services/panel-upgrades"
    }
  ];

  return (
    <section className="section bg-lightgray py-8 md:py-10">
      <div className="container-custom">
        <div className="text-center mb-8">
          <h2 className="mb-3">Our Electrical Services</h2>
          <p className="max-w-3xl mx-auto text-navy/80">
            Elevation Electrical provides comprehensive electrical services for both residential and commercial clients throughout Lake Helen, Florida and surrounding areas.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {services.map((service) => (
            <div key={service.id} className="card hover:-translate-y-1 group p-4">
              <div className="flex flex-col h-full">
                <div className="mb-3 transform group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-lg font-bold mb-2">{service.title}</h3>
                <p className="text-navy/70 text-sm mb-4 flex-grow">{service.description}</p>
                <Link to={service.link} className="text-orange font-semibold hover:text-orange-dark inline-flex items-center text-sm">
                  Explore {service.title}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 group-hover:ml-2 transition-all duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
        
        {/* Featured Services Showcase */}
        <div className="mt-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-5">
                <h3 className="text-xl font-bold mb-3">Panel Upgrades</h3>
                <p className="text-navy/70 mb-3">
                  Outdated electrical panels can pose safety risks and limit your home's electrical capacity. Our professional panel upgrades ensure your electrical system can safely handle modern power demands.
                </p>
                <div className="flex justify-between items-center">
                  <Link to="/services/panel-upgrades" className="text-orange font-semibold hover:text-orange-dark inline-flex items-center">
                    Explore panel upgrade services
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </div>
              </div>
              <div className="h-64 overflow-hidden">
                <RemoteImage
                  src={supabaseImgSrc(IMAGE_PATHS[PANEL_PATH], { width: 960, format: "webp" })}
                  srcSet={supabaseSrcSet(IMAGE_PATHS[PANEL_PATH], [320, 640, 960], "webp")}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  width={960}
                  height={640}
                  alt="Before and after electrical panel upgrade by Elevation Electrical"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-5">
                <h3 className="text-xl font-bold mb-3">Generator Installation</h3>
                <p className="text-navy/70 mb-3">
                  Never lose power again with a professionally installed backup generator. We provide complete generator solutions to keep your home or business running during outages.
                </p>
                <div className="flex justify-between items-center">
                  <Link to="/services/generators" className="text-orange font-semibold hover:text-orange-dark inline-flex items-center">
                    Explore generator installation
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </div>
              </div>
              <div className="h-64 overflow-hidden">
                <RemoteImage
                  src={supabaseImgSrc(IMAGE_PATHS[GEN_PATH], { width: 960, format: "webp" })}
                  srcSet={supabaseSrcSet(IMAGE_PATHS[GEN_PATH], [320, 640, 960], "webp")}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  width={960}
                  height={640}
                  alt="Whole-home generator installation"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;
